import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  // Download as DownloadIcon,
  // Share as ShareIcon,
  // Delete as DeleteIcon,
  Visibility as PreviewIcon,
  History as HistoryIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
  Analytics as AnalyticsIcon,
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  CompareArrows as CompareArrowsIcon,
  ViewCarousel as ViewCarouselIcon,
  CheckCircle as CheckCircleIcon,
  BrokenImage as BrokenImageIcon,
} from '@mui/icons-material';
import { formatDateSafe, getBestDate, formatFileSize as formatFileSizeUtil } from '../utils/dateUtils';
import { formatDocumentTitle } from '../utils/documentUtils';
import { extractSearchKeyword, resolveInitialPage } from '../utils/sourceNavigation';
import VersionHistoryView from '@components/documents/VersionHistoryView';
import PermissionManagementView from '@components/documents/PermissionManagementView';
import AnalysisHistoryView from '@components/documents/AnalysisHistoryView';
import PDFHighlightViewer from '@components/documents/PDFHighlightViewer';
import { documentService } from '../services/document.service';

// Get API URL from environment or use current origin
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const DocumentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL parameters for PDF highlight (e.g., ?highlight=search+term&page=3)
  const highlightParam = searchParams.get('highlight');
  const pageParam = searchParams.get('page');
  const approxPageParam = searchParams.get('approxPage');

  // Store source params in refs to persist after URL cleanup.
  const highlightTermsRef = useRef<{ documentId?: string; terms: string[] }>({
    documentId: id,
    terms: [],
  });
  const initialPageRef = useRef<{ documentId?: string; page: number; explicit: boolean }>({
    documentId: id,
    page: 1,
    explicit: false,
  });
  const preferredSearchPageRef = useRef<{ documentId?: string; page?: number }>({
    documentId: id,
  });

  // Parse highlight terms from URL parameter (only on initial load)
  // Bug fix: Extract keywords from query to show only important terms in search box
  const highlightTerms = useMemo(() => {
    if (highlightTermsRef.current.documentId !== id) {
      highlightTermsRef.current = { documentId: id, terms: [] };
    }
    if (!highlightParam) return highlightTermsRef.current.terms;
    const extractedQuery = extractSearchKeyword(highlightParam);
    const terms = extractedQuery.split(/\s+/).filter(term => term.length > 0);
    highlightTermsRef.current = { documentId: id, terms };
    return terms;
  }, [id, highlightParam]);

  // Parse initial page from URL parameter
  const initialPageState = useMemo(() => {
    const previousState = initialPageRef.current.documentId === id
      ? initialPageRef.current
      : { page: 1, explicit: false };
    const fallbackPage = previousState.page;
    const page = resolveInitialPage(pageParam, fallbackPage);
    const explicit = previousState.explicit || pageParam !== null;
    initialPageRef.current = { documentId: id, page, explicit };
    return { page, explicit };
  }, [id, pageParam]);
  const initialPage = initialPageState.page;

  const preferredSearchPage = useMemo(() => {
    if (preferredSearchPageRef.current.documentId !== id) {
      preferredSearchPageRef.current = { documentId: id };
    }
    if (!approxPageParam) return preferredSearchPageRef.current.page;

    const page = Number.parseInt(approxPageParam, 10);
    const nextPage = Number.isInteger(page) && page > 0 ? page : undefined;
    preferredSearchPageRef.current = { documentId: id, page: nextPage };
    return nextPage;
  }, [id, approxPageParam]);

  // Clear highlight and page params from URL after initial load (prevents persistence on reload)
  useEffect(() => {
    if (highlightParam || pageParam || approxPageParam) {
      // Use replace to update URL without adding to history
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('highlight');
      newParams.delete('page');
      newParams.delete('approxPage');
      setSearchParams(newParams, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [document, setDocument] = useState<any>(null);
  const [images, setImages] = useState<any[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [analyzingImages, setAnalyzingImages] = useState(false);
  const [analyzingImageId, setAnalyzingImageId] = useState<string | null>(null);
  const [forceReanalyze, setForceReanalyze] = useState(false);
  const [reExtracting, setReExtracting] = useState(false);

  // Image comparison and sequence analysis states
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [comparisonMode, setComparisonMode] = useState<'compare' | 'sequence' | null>(null);
  const [comparisonType, setComparisonType] = useState<'changes' | 'evolution' | 'differences'>('evolution');
  const [comparisonResult, setComparisonResult] = useState<string | null>(null);
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  const [comparingImages, setComparingImages] = useState(false);
  const getComparisonTypeLabel = (type: 'changes' | 'evolution' | 'differences') =>
    t(`documents.imageComparison.types.${type}`);

  // Document preview states (using Blob URL to handle authentication)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  // Ref to track if preview fetch is in progress (prevents useEffect dependency loop)
  const previewFetchingRef = useRef(false);

  // Image blob URLs (for authenticated image loading)
  const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
  // Track images that failed to load
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set());

  // Metadata re-extraction states
  const [reExtractingMetadata, setReExtractingMetadata] = useState(false);
  const [extractTitle, setExtractTitle] = useState(true);
  const [extractSummary, setExtractSummary] = useState(true);
  const [extractTags, setExtractTags] = useState(true);

  // Load document preview with authentication (iframes/images cannot send Authorization headers)
  // Uses AbortController to cancel pending requests when document ID changes (fixes race condition)
  const loadPreview = useCallback(async (signal?: AbortSignal) => {
    if (!id) return;

    // Set ref to indicate fetch is in progress
    previewFetchingRef.current = true;

    try {
      setPreviewLoading(true);
      setPreviewError(null);

      // Get auth token from sessionStorage
      const token = sessionStorage.getItem('access_token');

      // Build the preview URL - handle API_URL that may or may not have /api/v1
      let baseUrl = API_URL;
      if (baseUrl.endsWith('/api/v1')) {
        baseUrl = baseUrl.replace(/\/api\/v1$/, '');
      }
      const previewApiUrl = `${baseUrl}/api/v1/documents/${id}/preview`;

      // Fetch with authentication header and abort signal
      const response = await fetch(previewApiUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        signal,
      });

      // Check if request was aborted before processing response
      if (signal?.aborted) return;

      if (!response.ok) {
        // Try to get error message from JSON response
        let errorMessage = t('documents.previewLoadFailedWithStatus', { status: response.status });
        try {
          const errorData = await response.json();
          if (errorData.detail) {
            errorMessage = errorData.detail;
          }
        } catch {
          // If not JSON, use status text
          errorMessage = t('documents.previewLoadFailedWithStatusText', {
            status: response.status,
            statusText: response.statusText,
          });
        }
        throw new Error(errorMessage);
      }

      // Convert to Blob and create object URL
      const blob = await response.blob();

      // Check if request was aborted before setting state
      if (signal?.aborted) return;

      const blobUrl = URL.createObjectURL(blob);
      setPreviewUrl(blobUrl);
    } catch (err) {
      // Don't log or set error for aborted requests
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Failed to load document preview:', err);
      setPreviewError(err instanceof Error ? err.message : t('documents.previewLoadFailed'));
    } finally {
      // Reset ref to indicate fetch is no longer in progress
      previewFetchingRef.current = false;
      // Always reset loading state to prevent stuck loading state (fixes #226)
      // Even when aborted, we need to reset loading state to allow next load attempt
      setPreviewLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error/fallback copy; excluded so a language change doesn't refetch the document/preview (PR #790)
  }, [id]);

  // Cleanup Blob URL on unmount or when document changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Reset preview state and cancel pending requests when document ID changes (fixes #211)
  // This ensures that when navigating from one document to another,
  // the preview is reloaded for the new document without race conditions
  useEffect(() => {
    // Reset preview states for new document
    setPreviewUrl(null);
    setPreviewLoading(false);
    setPreviewError(null);
    // IMPORTANT: Reset fetching ref to allow new fetch to start (fixes #226)
    // When document ID changes, the previous fetch's finally block may not have executed yet
    // due to async nature of abort. This ensures the new fetch can start immediately.
    previewFetchingRef.current = false;

    // Create abort controller for this effect
    const abortController = new AbortController();

    // Return cleanup function that aborts pending requests
    return () => {
      abortController.abort();
    };
  }, [id]);

  // Cleanup image blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(imageBlobUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageBlobUrls]);

  // Load image blob URLs when images change
  useEffect(() => {
    // Bounded concurrency: a document with many extracted figures (PDFs
    // with diagrams) used to fire N parallel /documents/{id}/images/{id}
    // requests via Promise.all, easily exhausting the per-user rate-limit
    // budget (100 req/60s) and producing a 429 cascade. Batch in chunks of
    // IMAGE_BLOB_CONCURRENCY so a 200-image document loads in waves of 4
    // rather than one burst.
    const IMAGE_BLOB_CONCURRENCY = 4;

    const loadImageBlobs = async () => {
      if (!id || images.length === 0) return;

      const results: Array<{ id: string; url: string; failed: boolean }> = [];
      for (let i = 0; i < images.length; i += IMAGE_BLOB_CONCURRENCY) {
        const batch = images.slice(i, i + IMAGE_BLOB_CONCURRENCY);
        const batchResults = await Promise.all(
          batch.map(async (image) => {
            try {
              const blobUrl = await documentService.getDocumentImageBlob(id, image.id);
              return { id: image.id, url: blobUrl, failed: false };
            } catch (err) {
              console.error(`Failed to load image blob for ${image.id}:`, err);
              return { id: image.id, url: '', failed: true };
            }
          })
        );
        results.push(...batchResults);
      }

      const newBlobUrls: Record<string, string> = {};
      const newFailedIds = new Set<string>();

      results.forEach(({ id, url, failed }) => {
        if (url) {
          newBlobUrls[id] = url;
        }
        if (failed) {
          newFailedIds.add(id);
        }
      });

      // Revoke old URLs before setting new ones
      Object.values(imageBlobUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });

      setImageBlobUrls(newBlobUrls);
      setFailedImageIds(newFailedIds);
    };

    loadImageBlobs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, images]);

  const loadImages = useCallback(async () => {
    if (!id) return;

    try {
      setImagesLoading(true);
      const imageData = await documentService.getDocumentImages(id);
      setImages(imageData.images || []);
    } catch (err) {
      console.error('Failed to load images:', err);
      // If images fail to load, just set empty array (document might not have images)
      setImages([]);
    } finally {
      setImagesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const fetchDocument = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch actual document from API
        const doc = await documentService.getDocument(id);

        // Transform API response to match component needs
        const transformedDocument = {
          id: doc.id,
          title: doc.title || doc.fileName || '',
          description: doc.description || '',
          summary: doc.summary || '',
          category: doc.category,
          tags: doc.tags || [],
          uploadedBy: doc.createdByName || doc.createdBy || t('common.unknown'),
          uploadedAt: doc.uploadedAt || doc.createdAt,
          fileType: doc.mimeType?.split('/').pop() || doc.currentVersion?.mimeType?.split('/').pop() || 'unknown',
          size: doc.fileSize || doc.currentVersion?.fileSize || 0,
          version: doc.version || doc.currentVersion?.versionNumber || 1,
          thumbnail: null,
          status: doc.status || 'active',
          permissions: {
            canEdit: true,
            canDelete: true,
            canShare: true,
            canDownload: true,
          },
          metadata: {
            fileName: doc.fileName || doc.currentVersion?.fileName || '',
            mimeType: doc.mimeType || doc.currentVersion?.mimeType || '',
            path: doc.path || '',
            lastModified: doc.updatedAt || doc.createdAt,
          },
          analysisHistory: (doc as any).analysis_history || []
        };

        setDocument(transformedDocument);
        setLoading(false);
      } catch (err) {
        setError(t('documents.failedToLoad'));
        setLoading(false);
      }
    };

    if (id) {
      fetchDocument();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- t only builds error/fallback copy; excluded so a language change doesn't refetch the document/preview (PR #790)
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 1) {
      loadImages();
    }
  }, [id, activeTab, loadImages]);

  // Load preview when preview tab is active and document is loaded
  // Uses AbortController to cancel pending requests when dependencies change
  // IMPORTANT: Uses ref instead of state (previewLoading) to prevent abort loop
  // When previewLoading was in dependency array, setting it to true would trigger
  // useEffect cleanup, immediately aborting the request (Issue #220)
  // NOTE: The finally block in loadPreview now always resets previewLoading (fixes #226)
  // This ensures that even if a request is aborted, the loading state is reset,
  // allowing the next effect run to start a new fetch
  // CRITICAL FIX (Issue #226 race condition): When document changes (e.g., document fetch completes
  // while switching documents via chat), the cleanup function must reset previewFetchingRef
  // SYNCHRONOUSLY before the new effect runs. Otherwise, the new effect sees the ref as true
  // (set by the previous loadPreview call) and skips fetching the preview.
  useEffect(() => {
    const abortController = new AbortController();

    // Use ref to check if fetch is in progress (doesn't cause re-render)
    if (id && document && activeTab === 0 && !previewUrl && !previewFetchingRef.current && !previewError) {
      loadPreview(abortController.signal);
    }

    return () => {
      abortController.abort();
      // IMPORTANT: Reset fetching ref synchronously in cleanup (fixes #226)
      // This ensures that when the effect re-runs due to dependency changes
      // (especially when `document` changes after document fetch completes),
      // the new effect can start a fresh preview fetch immediately.
      // Without this, the ref would remain true from the aborted fetch's async
      // finally block, causing the condition check to fail.
      previewFetchingRef.current = false;
    };
  }, [id, document, activeTab, previewUrl, previewError, loadPreview]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBackClick = () => {
    // Use replace to prevent highlight URL from remaining in browser history
    navigate('/documents', { replace: true });
  };

  const handleAnalyzeImage = async (imageId: string) => {
    if (!id) return;

    try {
      setAnalyzingImageId(imageId);
      const result = await documentService.analyzeImage(id, imageId);

      // Update image in local state
      setImages(prevImages =>
        prevImages.map(img =>
          img.id === imageId
            ? { ...img, aiDescription: result.aiDescription }
            : img
        )
      );

      alert(t('documents.imageAnalysisComplete'));
    } catch (error: any) {
      console.error('Failed to analyze image:', error);
      const errorMessage = error.response?.data?.detail || t('documents.imageAnalysisFailed');
      alert(errorMessage);
    } finally {
      setAnalyzingImageId(null);
    }
  };

  const handleAnalyzeAllImages = async () => {
    if (!id) return;

    try {
      setAnalyzingImages(true);
      const result = await documentService.analyzeAllImages(id, forceReanalyze);

      // Update all images in local state
      result.results.forEach(r => {
        if ((r.status === 'success' || r.status === 'skipped') && r.aiDescription) {
          setImages(prevImages =>
            prevImages.map(img =>
              img.id === r.imageId
                ? { ...img, aiDescription: r.aiDescription }
                : img
            )
          );
        }
      });

      alert(t(forceReanalyze ? 'documents.imagesReanalyzed' : 'documents.imagesAnalyzed', {
        count: result.analyzedImages,
      }));
    } catch (error: any) {
      console.error('Failed to analyze images:', error);
      const errorMessage = error.response?.data?.detail || t('documents.imageAnalysisFailed');
      alert(errorMessage);
    } finally {
      setAnalyzingImages(false);
    }
  };

  const handleReExtractStandard = async () => {
    if (!id) return;

    try {
      setReExtracting(true);
      const result = await documentService.reExtractImages(id, {
        asBlueprint: false,
        runAiAnalysis: false,
      });

      alert(t('documents.imagesExtracted', { count: result.data.images_saved }));

      // Reload images
      await loadImages();
    } catch (error) {
      console.error('Failed to re-extract images:', error);
      alert(t('documents.imageReExtractFailed'));
    } finally {
      setReExtracting(false);
    }
  };

  const handleReExtractBlueprint = async () => {
    if (!id) return;

    try {
      setReExtracting(true);
      const result = await documentService.reExtractImages(id, {
        asBlueprint: true,
        dpi: 150,
        runAiAnalysis: false,
      });

      alert(t('documents.blueprintImagesExtracted', { count: result.data.images_saved }));

      // Reload images
      await loadImages();
    } catch (error) {
      console.error('Failed to re-extract blueprint images:', error);
      alert(t('documents.blueprintReExtractFailed'));
    } finally {
      setReExtracting(false);
    }
  };

  // Re-extract metadata using LLM
  const handleReExtractMetadata = async () => {
    if (!id) return;

    // Check if at least one option is selected
    if (!extractTitle && !extractSummary && !extractTags) {
      alert(t('documents.selectAtLeastOneMetadataField'));
      return;
    }

    try {
      setReExtractingMetadata(true);
      const result = await documentService.reExtractMetadata(id, {
        extractTitle,
        extractSummary,
        extractTags,
      });

      // Update local document state with new values
      if (result.updated_fields.length > 0) {
        setDocument((prev: any) => ({
          ...prev,
          ...(result.title && { title: result.title }),
          ...(result.summary && { summary: result.summary }),
          ...(result.tags && { tags: result.tags }),
        }));
      }

      alert(result.message);
    } catch (error: any) {
      console.error('Failed to re-extract metadata:', error);
      const errorMessage = error.message || t('documents.metadataReExtractFailed');
      alert(errorMessage);
    } finally {
      setReExtractingMetadata(false);
    }
  };

  // Toggle image selection
  const handleToggleImageSelection = (imageId: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageId)) {
        return prev.filter(id => id !== imageId);
      } else {
        return [...prev, imageId];
      }
    });
  };

  // Clear image selection
  const handleClearSelection = () => {
    setSelectedImages([]);
    setComparisonMode(null);
  };

  // Start comparison mode
  const handleStartComparison = (mode: 'compare' | 'sequence') => {
    if (mode === 'compare' && selectedImages.length !== 2) {
      alert(t('documents.selectTwoImages'));
      return;
    }
    if (mode === 'sequence' && selectedImages.length < 2) {
      alert(t('documents.selectAtLeastTwoImages'));
      return;
    }
    if (mode === 'sequence' && selectedImages.length > 10) {
      alert(t('documents.sequenceAnalysisMaxImages', { count: 10 }));
      return;
    }
    setComparisonMode(mode);
    handleExecuteComparison(mode);
  };

  // Execute comparison or sequence analysis
  const handleExecuteComparison = async (mode: 'compare' | 'sequence') => {
    if (!id) return;

    try {
      setComparingImages(true);
      setComparisonResult(null);
      setShowComparisonDialog(true);

      if (mode === 'compare') {
        // Compare two images
        const result = await documentService.compareImages(
          id,
          selectedImages[0],
          selectedImages[1],
          comparisonType
        );
        setComparisonResult(result.analysis);
      } else {
        // Analyze image sequence
        // Sort images by page number
        const sortedImageIds = [...selectedImages].sort((a, b) => {
          const imgA = images.find(img => img.id === a);
          const imgB = images.find(img => img.id === b);
          return (imgA?.pageNumber || 0) - (imgB?.pageNumber || 0);
        });

        const result = await documentService.analyzeImageSequence(id, sortedImageIds);
        setComparisonResult(result.analysis);
      }
    } catch (error: any) {
      console.error('Failed to analyze images:', error);
      const errorMessage = error.response?.data?.detail || t('documents.imageAnalysisFailed');
      alert(errorMessage);
      setShowComparisonDialog(false);
    } finally {
      setComparingImages(false);
    }
  };

  // Close comparison dialog
  const handleCloseComparisonDialog = () => {
    setShowComparisonDialog(false);
    setComparisonResult(null);
    setComparisonMode(null);
    handleClearSelection();
  };


  // Use formatFileSizeUtil from utils instead

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !document) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || t('documents.documentNotFound')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackClick}
          sx={{ mt: 2 }}
        >
          {t('documents.returnToDocuments')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/documents"
            onClick={(e) => {
              e.preventDefault();
              navigate('/documents');
            }}
            sx={{ cursor: 'pointer' }}
          >
            {t('navigation.documents')}
          </Link>
          <Typography color="text.primary">{formatDocumentTitle(document.title, document.metadata?.fileName || document.fileName)}</Typography>
        </Breadcrumbs>

        {/* Document Header */}
        <Paper sx={{ p: 3, overflow: 'hidden' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{
              flexDirection: { xs: 'column', lg: 'row' },
              gap: { xs: 2, lg: 0 }
            }}
          >
            <Box flex={1} sx={{ minWidth: 0, overflow: 'hidden' }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton onClick={handleBackClick} sx={{ mr: 1 }}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto',
                    lineHeight: 1.2
                  }}
                >
                  {formatDocumentTitle(document.title, document.metadata?.fileName || document.fileName)}
                </Typography>
              </Box>

              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {document.summary || document.description}
              </Typography>

              <Box
                display="flex"
                gap={1}
                mb={2}
                sx={{
                  flexWrap: 'wrap',
                  maxWidth: '80%'
                }}
              >
                <Chip label={document.category} color="primary" variant="outlined" />
                {document.tags.slice(0, 10).map((tag: string) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{
                      maxWidth: '200px',
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                ))}
                {document.tags.length > 10 && (
                  <Chip
                    label={t('common.moreCount', { count: document.tags.length - 10 })}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                )}
              </Box>

              <Box display="flex" alignItems="center" gap={2}>
                <Avatar sx={{ width: 32, height: 32 }}>
                  {document.uploadedBy.split(' ').map((n: string) => n[0]).join('')}
                </Avatar>
                <Box>
                  <Typography variant="body2">
                    {t('documents.uploadedBy')} <strong>{document.uploadedBy}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateSafe(getBestDate(document), 'yyyy/MM/dd HH:mm')} • {formatFileSizeUtil(document.size || document.fileSize)} • {document.version ? `v${document.version}` : 'v1'}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Action Buttons */}
            {/* <Box display="flex" gap={1}>
              {document.permissions.canDownload && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                </Button>
              )}
              {document.permissions.canShare && (
                <Button
                  variant="outlined"
                  startIcon={<ShareIcon />}
                  onClick={handleShare}
                >
                </Button>
              )}
              {document.permissions.canDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDelete}
                >
                </Button>
              )}
            </Box> */}
          </Box>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label={t('documents.detailTabsAria')}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PreviewIcon />} label={t('documents.preview')} />
          <Tab icon={<InfoIcon />} label={t('documents.metadata')} />
          <Tab icon={<AnalyticsIcon />} label={t('documents.analysisHistory')} />
          <Tab icon={<HistoryIcon />} label={t('documents.versionHistory')} />
          <Tab icon={<SecurityIcon />} label={t('documents.permissions')} />
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* Document Preview */}
          <Box>
            {/* Loading state */}
            {previewLoading && (
              <Box
                sx={{
                  height: 400,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                }}
              >
                <CircularProgress size={40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('documents.loadingPreview', 'Loading preview...')}
                </Typography>
              </Box>
            )}
            {/* Error state */}
            {previewError && !previewLoading && (
              <Box sx={{ mb: 2 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {previewError}
                </Alert>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {/* Check for file-not-found errors (both Japanese and English) */}
                  {(() => {
                    const isFileNotFound = previewError.includes('見つかりません') ||
                      previewError.toLowerCase().includes('not found') ||
                      previewError.includes('404');
                    return (
                      <>
                        {/* Show retry button only for non-404 errors */}
                        {!isFileNotFound && (
                          <Button variant="outlined" onClick={() => loadPreview()}>
                            {t('common.retry', 'Retry')}
                          </Button>
                        )}
                        {/* Show re-upload suggestion for file not found errors */}
                        {isFileNotFound && (
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/documents/upload')}
                          >
                            {t('documents.uploadNewVersion')}
                          </Button>
                        )}
                      </>
                    );
                  })()}
                </Box>
              </Box>
            )}
            {/* Preview content */}
            {!previewLoading && !previewError && previewUrl && (
              <>
                {document?.fileType === 'pdf' ? (
                  <Box
                    sx={{
                      width: '100%',
                      height: 650,
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  >
                    <PDFHighlightViewer
                      pdfUrl={previewUrl}
                      highlightTerms={highlightTerms}
                      initialPage={initialPage}
                      initialPageExplicit={initialPageState.explicit}
                      preferredSearchPage={preferredSearchPage}
                    />
                  </Box>
                ) : document?.fileType === 'png' || document?.fileType === 'jpg' || document?.fileType === 'jpeg' || document?.fileType === 'gif' ? (
                  <Box textAlign="center">
                    <img
                      src={previewUrl}
                      alt={document.title}
                      style={{ maxWidth: '100%', maxHeight: 600, objectFit: 'contain' }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 400,
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      {t('documents.previewUnavailableForType', {
                        type: document?.fileType || t('documents.fileTypeFallback'),
                      })}
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            {t('documents.metadata')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 2 }}>
            <Typography variant="subtitle2">{t('documents.fileName')}:</Typography>
            <Typography>
              {/* Avoid double extension if title already includes extension */}
              {document.title?.toLowerCase().endsWith(`.${document.fileType?.toLowerCase()}`)
                ? document.title
                : `${document.title}.${document.fileType}`}
            </Typography>

            <Typography variant="subtitle2">{t('documents.fileType')}:</Typography>
            <Typography>{document.fileType.toUpperCase()}</Typography>

            <Typography variant="subtitle2">{t('documents.fileSize')}:</Typography>
            <Typography>{formatFileSizeUtil(document.size || document.fileSize)}</Typography>

            <Typography variant="subtitle2">{t('documents.category')}:</Typography>
            <Typography>{document.category}</Typography>

            <Typography variant="subtitle2">{t('documents.department')}:</Typography>
            <Typography>{document.metadata.department}</Typography>

            <Typography variant="subtitle2">{t('documents.classification')}:</Typography>
            <Typography>{document.metadata.classification}</Typography>

            <Typography variant="subtitle2">{t('documents.status')}:</Typography>
            <Chip
              label={document.status}
              size="small"
              color="success"
              sx={{ width: 'fit-content', minWidth: 0, px: 1, fontSize: '0.9rem' }}
            />

            <Typography variant="subtitle2">{t('documents.createdAt')}:</Typography>
            <Typography>
              {formatDateSafe(getBestDate(document), 'yyyy/MM/dd HH:mm')}
            </Typography>

            <Typography variant="subtitle2">{t('documents.lastModified')}:</Typography>
            <Typography>
              {formatDateSafe(document.metadata?.lastModified || getBestDate(document), 'yyyy/MM/dd HH:mm')}
            </Typography>

            <Typography variant="subtitle2">{t('documents.retentionPeriod')}:</Typography>
            <Typography>{document.metadata.retentionPeriod}</Typography>
          </Box>

          <Box sx={{ mt: 4, p: 2, backgroundColor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeIcon color="info" />
              {t('documents.reExtractMetadata')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('documents.reExtractMetadataDescription')}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={extractTitle}
                  onChange={(e) => setExtractTitle(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">{t('documents.regenerateTitle')}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={extractSummary}
                  onChange={(e) => setExtractSummary(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">{t('documents.regenerateSummary')}</Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <Checkbox
                  checked={extractTags}
                  onChange={(e) => setExtractTags(e.target.checked)}
                  size="small"
                />
                <Typography variant="body2">{t('documents.regenerateTags')}</Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              color="info"
              startIcon={reExtractingMetadata ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
              onClick={handleReExtractMetadata}
              disabled={reExtractingMetadata || (!extractTitle && !extractSummary && !extractTags)}
            >
              {reExtractingMetadata ? t('documents.reExtractingMetadata') : t('documents.reExtractMetadata')}
            </Button>
          </Box>

          <Box sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                {t('documents.extractedImagesTitle')}{' '}
                {images.length > 0 && t('documents.itemCount', { count: images.length })}
              </Typography>
              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                {images.length > 0 && (
                  <>
                    <Button
                      variant="contained"
                      startIcon={analyzingImages ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                      onClick={handleAnalyzeAllImages}
                      disabled={analyzingImages}
                    >
                      {analyzingImages ? t('documents.aiAnalyzing') : t('documents.analyzeAllImages')}
                    </Button>
                    <Box display="flex" alignItems="center">
                      <input
                        type="checkbox"
                        id="force-reanalyze"
                        checked={forceReanalyze}
                        onChange={(e) => setForceReanalyze(e.target.checked)}
                        style={{ marginRight: '4px' }}
                      />
                      <label htmlFor="force-reanalyze" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
                        {t('documents.forceReanalyze')}
                      </label>
                    </Box>
                  </>
                )}
                <Box display="flex" gap={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleReExtractStandard}
                    disabled={reExtracting}
                  >
                    {reExtracting ? t('documents.extracting') : t('documents.reExtractImages')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={handleReExtractBlueprint}
                    disabled={reExtracting}
                  >
                    {reExtracting ? t('documents.extracting') : t('documents.reExtractAsBlueprint')}
                  </Button>
                </Box>
              </Box>

              {/* Image comparison controls */}
              {images.length >= 2 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="info.dark" mb={1}>
                    {t('documents.imageComparison.title')}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    {selectedImages.length > 0 && (
                      <Chip
                        label={t('documents.imagesSelected', { count: selectedImages.length })}
                        color="primary"
                        size="small"
                        onDelete={handleClearSelection}
                      />
                    )}

                    {selectedImages.length === 2 && (
                      <>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>{t('documents.imageComparison.type')}</InputLabel>
                          <Select
                            value={comparisonType}
                            label={t('documents.imageComparison.type')}
                            onChange={(e) => setComparisonType(e.target.value as any)}
                          >
                            <MenuItem value="changes">{getComparisonTypeLabel('changes')}</MenuItem>
                            <MenuItem value="evolution">{getComparisonTypeLabel('evolution')}</MenuItem>
                            <MenuItem value="differences">{getComparisonTypeLabel('differences')}</MenuItem>
                          </Select>
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<CompareArrowsIcon />}
                          onClick={() => handleStartComparison('compare')}
                        >
                          {t('documents.imageComparison.compareTwo')}
                        </Button>
                      </>
                    )}

                    {selectedImages.length >= 2 && selectedImages.length <= 10 && (
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        startIcon={<ViewCarouselIcon />}
                        onClick={() => handleStartComparison('sequence')}
                      >
                        {t('documents.imageComparison.sequenceAnalysis', { count: selectedImages.length })}
                      </Button>
                    )}

                    {selectedImages.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('documents.imageComparison.selectImagesHint')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
            {imagesLoading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress size={30} />
              </Box>
            ) : images.length > 0 ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2, mt: 2 }}>
                {images.map((image) => {
                  const isSelected = selectedImages.includes(image.id);
                  return (
                    <Paper
                      key={image.id}
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.2s',
                        border: isSelected ? '2px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'primary.lighter' : 'background.paper',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                    >
                      {/* Selection checkbox */}
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleToggleImageSelection(image.id)}
                            size="small"
                            icon={<CheckCircleIcon />}
                            checkedIcon={<CheckCircleIcon />}
                          />
                          <Typography variant="caption" fontWeight="bold">
                            {t('common.select')}
                          </Typography>
                        </Box>
                        {isSelected && (
                          <Chip
                            label={`#${selectedImages.indexOf(image.id) + 1}`}
                            size="small"
                            color="primary"
                          />
                        )}
                      </Box>

                      <Box
                        component="img"
                        src={imageBlobUrls[image.id] || ''}
                        alt={t('documents.extractedImageAlt', {
                          page: image.pageNumber,
                          index: image.imageIndex + 1,
                        })}
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'contain',
                          backgroundColor: 'grey.100',
                          borderRadius: 1,
                          cursor: 'pointer',
                          mb: 1,
                          display: imageBlobUrls[image.id] ? 'block' : 'none',
                        }}
                        loading="lazy"
                        onClick={() => imageBlobUrls[image.id] && window.open(imageBlobUrls[image.id], '_blank')}
                      />
                      {!imageBlobUrls[image.id] && (
                        <Box
                          sx={{
                            width: '100%',
                            height: 200,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: failedImageIds.has(image.id) ? 'error.lighter' : 'grey.100',
                            borderRadius: 1,
                            mb: 1,
                            border: failedImageIds.has(image.id) ? '1px solid' : 'none',
                            borderColor: 'error.light',
                          }}
                        >
                          {failedImageIds.has(image.id) ? (
                            <>
                              <BrokenImageIcon sx={{ color: 'error.main', fontSize: 40, mb: 1 }} />
                              <Typography variant="caption" color="error.main" textAlign="center">
                                {t('documents.imageLoadFailed')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" textAlign="center">
                                {t('documents.imageFileNotFound')}
                              </Typography>
                            </>
                          ) : (
                            <CircularProgress size={24} />
                          )}
                        </Box>
                      )}
                    <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                      {t('documents.imagePageCaption', {
                        page: image.pageNumber,
                        index: image.imageIndex + 1,
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mb: 1 }}>
                      {image.width} × {image.height} • {(image.fileSize / 1024).toFixed(1)} KB
                    </Typography>

                    {image.aiDescription ? (
                      <Box sx={{ mt: 1 }}>
                        <Box sx={{ p: 1, backgroundColor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
                          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                            <PsychologyIcon fontSize="small" color="info" />
                            <Typography variant="caption" fontWeight="bold" color="info.main">
                              {t('documents.aiAnalysisResult')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                            {image.aiDescription}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={analyzingImageId === image.id ? <CircularProgress size={16} /> : <PsychologyIcon />}
                          onClick={() => handleAnalyzeImage(image.id)}
                          disabled={analyzingImageId === image.id}
                          sx={{ mt: 0.5 }}
                        >
                          {analyzingImageId === image.id ? t('documents.analyzing') : t('documents.reanalyze')}
                        </Button>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={analyzingImageId === image.id ? <CircularProgress size={16} /> : <PsychologyIcon />}
                        onClick={() => handleAnalyzeImage(image.id)}
                        disabled={analyzingImageId === image.id}
                        sx={{ mt: 1 }}
                      >
                        {analyzingImageId === image.id ? t('documents.analyzing') : t('documents.aiAnalysis')}
                      </Button>
                    )}
                    </Paper>
                  );
                })}
              </Box>
            ) : (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'grey.300',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('documents.noExtractedImages')}
                </Typography>
              </Box>
            )}
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Analysis History */}
          <AnalysisHistoryView analysisHistory={document.analysisHistory} />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {/* Version History */}
          <Typography variant="h6" gutterBottom>
            {t('documents.versionHistory')}
          </Typography>
          <VersionHistoryView documentId={id} analysisHistory={document.analysisHistory} />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {/* Permissions */}
          <Typography variant="h6" gutterBottom>
            {t('documents.documentPermissions')}
          </Typography>
          <PermissionManagementView documentId={id} />
        </TabPanel>
      </Paper>

      {/* Comparison Result Dialog */}
      <Dialog
        open={showComparisonDialog}
        onClose={handleCloseComparisonDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            {comparisonMode === 'compare' ? (
              <>
                <CompareArrowsIcon color="primary" />
                <Typography variant="h6">{t('documents.imageComparison.compareResultTitle')}</Typography>
              </>
            ) : (
              <>
                <ViewCarouselIcon color="secondary" />
                <Typography variant="h6">{t('documents.imageComparison.sequenceResultTitle')}</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {comparingImages ? (
            <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={4}>
              <CircularProgress size={50} />
                  <Typography variant="body2" color="text.secondary">
                {t('documents.imageComparison.aiAnalyzingPleaseWait')}
              </Typography>
            </Box>
          ) : comparisonResult ? (
            <Box>
              {comparisonMode === 'compare' && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    {t('documents.imageComparison.typeWithValue', {
                      type: getComparisonTypeLabel(comparisonType),
                    })}
                  </Typography>
                </Box>
              )}
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                {comparisonResult}
              </Typography>
            </Box>
          ) : (
            <Alert severity="error">
              {t('documents.imageComparison.resultLoadFailed')}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseComparisonDialog} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentDetailPage;
